import React, { FC, useState } from 'react';
import { Box, Button, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { setShippingAddress } from '@store/slices/order';
import { CustomerAddress } from '@models';
import { useGetSuburbHoursQuery } from '@services';
import { LoadingButton } from '@mui/lab';
import { showToast } from '@store/slices/notification';

type Props = {
  handleNext: () => void;
  onClose: () => void;
  addNewAddress: () => void;
};

export const AddressSelection: FC<Props> = ({ handleNext, onClose, addNewAddress }) => {
  const addresses = useAppSelector((state) => {
    return state.customer.value.selectedCustomer?.addresses;
  });

  const dispatch = useAppDispatch();

  const [selectedAddress, setSelectedAddress] = useState<CustomerAddress>();
  const [addressId, setAddressId] = useState('');

  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const address = addresses?.find((address: CustomerAddress) => address.id === Number(event.target.value)) ?? '';

    setAddressId(event.target.value);

    if (address !== '') {
      setSelectedAddress(address);
    }
  };

  /** Capture suburb and postcode from selected shipping address */
  const suburbString =
    selectedAddress !== undefined
      ? `${selectedAddress?.postal_code}_${selectedAddress?.city.toLowerCase().replace(/\s/g, '')}`
      : '2000_sydney';

  const { data, isFetching, isError } = useGetSuburbHoursQuery({ suburb: suburbString });

  if (isError) {
    dispatch(
      showToast({
        message: 'Jimmy Brings is not currently opened in this suburb!',
        type: 'error',
      }),
    );
  }

  const avaServiceCheck = (address: CustomerAddress) => {
    if (data && addressId !== '') {
      dispatch(setShippingAddress(address));
      handleNext();
    }
  };

  return (
    <Box padding={2}>
      <Grid container data-testid="address-selection">
        {addresses?.map((address: CustomerAddress) => (
          <Grid item xs={12} key={address.id}>
            <Box padding={1} marginBottom={1} sx={{ backgroundColor: '#F2F2F2' }} borderRadius="15px">
              <FormControl>
                <RadioGroup value={addressId} onChange={handleAddressChange}>
                  <FormControlLabel
                    control={<Radio />}
                    value={address.id}
                    name="customer-address"
                    label={
                      <Typography variant="body1">
                        {address.address2 ? `${address.address1}, ${address.address2}` : address.address1} {address.city}{' '}
                        {address.state_or_province}, {address.postal_code}
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center">
            <Button
              color="info"
              onClick={() => {
                onClose();
                addNewAddress();
              }}
              data-testid="add-new-address-btn"
            >
              Add new address
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <LoadingButton
              variant="contained"
              loading={isFetching}
              disabled={isError || !data || addressId === ''}
              loadingIndicator="Checking..."
              onClick={() => (selectedAddress ? avaServiceCheck(selectedAddress) : undefined)}
              data-testid="confirm-address-btn"
            >
              Confirm Address
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
